import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const StepProgressIndicator = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const steps = [
    {
      stepId: 1,
      label: 'Setup',
      route: '/location-input',
      description: 'Define participants and transportation'
    },
    {
      stepId: 2,
      label: 'Results',
      route: '/meeting-point-results',
      description: 'View calculated meeting points'
    },
    {
      stepId: 3,
      label: 'Refine',
      route: '/location-type-filter',
      description: 'Filter by location preferences'
    },
    {
      stepId: 4,
      label: 'Finalize',
      route: '/final-meeting-details',
      description: 'Review coordination details'
    }
  ];

  const currentStepIndex = steps?.findIndex(step => step?.route === location?.pathname);
  const currentStep = currentStepIndex !== -1 ? currentStepIndex + 1 : 1;

  const handleStepClick = (step, index) => {
    if (index < currentStepIndex) {
      navigate(step?.route);
    }
  };

  const isStepAccessible = (index) => {
    return index <= currentStepIndex;
  };

  const isStepComplete = (index) => {
    return index < currentStepIndex;
  };

  const isStepCurrent = (index) => {
    return index === currentStepIndex;
  };

  return (
    <div className="w-full bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.stepId}>
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => handleStepClick(step, index)}
                    disabled={!isStepAccessible(index)}
                    className={`
                      flex items-center justify-center w-12 h-12 rounded-full border-2 
                      transition-smooth hover-lift active-press
                      ${isStepComplete(index) 
                        ? 'bg-primary border-primary text-primary-foreground' 
                        : isStepCurrent(index)
                        ? 'bg-background border-primary text-primary' :'bg-muted border-border text-muted-foreground'
                      }
                      ${isStepAccessible(index) && !isStepCurrent(index) ? 'cursor-pointer' : 'cursor-default'}
                      focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3
                    `}
                    aria-label={`Step ${step?.stepId}: ${step?.label}`}
                    aria-current={isStepCurrent(index) ? 'step' : undefined}
                  >
                    {isStepComplete(index) ? (
                      <Icon name="Check" size={20} strokeWidth={2.5} />
                    ) : (
                      <span className="font-heading font-semibold">{step?.stepId}</span>
                    )}
                  </button>
                  <div className="mt-3 text-center">
                    <p className={`
                      font-heading text-sm font-medium
                      ${isStepCurrent(index) ? 'text-foreground' : 'text-muted-foreground'}
                    `}>
                      {step?.label}
                    </p>
                    <p className="caption text-muted-foreground mt-1 max-w-[120px]">
                      {step?.description}
                    </p>
                  </div>
                </div>
                {index < steps?.length - 1 && (
                  <div className="flex-1 h-0.5 mx-4 mb-12">
                    <div 
                      className={`
                        h-full transition-smooth
                        ${isStepComplete(index) ? 'bg-primary' : 'bg-border'}
                      `}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="md:hidden">
          <div className="flex items-center justify-between">
            <button
              onClick={() => currentStepIndex > 0 && navigate(steps?.[currentStepIndex - 1]?.route)}
              disabled={currentStepIndex === 0}
              className={`
                p-2 rounded-lg transition-smooth
                ${currentStepIndex > 0 
                  ? 'text-primary hover:bg-muted active-press' :'text-muted-foreground cursor-not-allowed'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3
              `}
              aria-label="Previous step"
            >
              <Icon name="ChevronLeft" size={24} />
            </button>

            <div className="flex flex-col items-center flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className={`
                  flex items-center justify-center w-10 h-10 rounded-full border-2 
                  bg-background border-primary text-primary
                `}>
                  <span className="font-heading font-semibold">{currentStep}</span>
                </div>
                <span className="caption text-muted-foreground">of {steps?.length}</span>
              </div>
              <p className="font-heading text-base font-medium text-foreground">
                {steps?.[currentStepIndex]?.label}
              </p>
              <p className="caption text-muted-foreground mt-1">
                {steps?.[currentStepIndex]?.description}
              </p>
            </div>

            <button
              onClick={() => currentStepIndex < steps?.length - 1 && navigate(steps?.[currentStepIndex + 1]?.route)}
              disabled={currentStepIndex === steps?.length - 1}
              className={`
                p-2 rounded-lg transition-smooth
                ${currentStepIndex < steps?.length - 1
                  ? 'text-primary hover:bg-muted active-press' :'text-muted-foreground cursor-not-allowed'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3
              `}
              aria-label="Next step"
            >
              <Icon name="ChevronRight" size={24} />
            </button>
          </div>

          <div className="flex gap-2 mt-4 justify-center">
            {steps?.map((_, index) => (
              <div
                key={index}
                className={`
                  h-1 rounded-full transition-smooth
                  ${index === currentStepIndex ? 'w-8 bg-primary' : 'w-2 bg-border'}
                `}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepProgressIndicator;